import axios from 'axios';
import JSZip from 'jszip';
import { buildGoColorsWorkbookArray } from './goColorsExcel';
import { resolveGoColorsDesignation } from './goColorsDesignation';

/*
 * The Go Colors payroll package: one folder of documents per candidate plus the candidate
 * Excel (goColorsExcel.js owns its columns). Shared by the Selected tab's "Generate
 * Report" (one screening, ticked candidates) and the "Download" button on the job page (every
 * screening of the job post) and on the job list (every EBO or every LFS job post).
 *
 * Everything is built in the browser, and JSZip holds the whole archive in memory (~3x its
 * size at peak), so a large set is split into parts of MAX_CANDIDATES_PER_ZIP and document
 * downloads run FETCH_CONCURRENCY candidates at a time.
 */

export const MAX_CANDIDATES_PER_ZIP = 40;
const FETCH_CONCURRENCY = 4;

const PAN_PATTERN = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i;
const AADHAAR_PATTERN = /^\d{12}$/;

/**
 * The Excel row and the documents to fetch for one candidate.
 *
 * @param {Object} c - candidate row (table row or payroll-export row)
 * @param {Array} documents - that candidate's submissions, newest first (documentStatusMap shape)
 * @param {Object} salaryData - { designation, doj, grossSalary } or {}
 * @param {Object} screeningMeta - { jobTitle, storeFormat } for the designation
 * @param {Object} [docCounts] - { uploaded, total } against the requested documents, as the table's Documents column
 */
export function buildGoColorsPayrollEntry(c, documents, salaryData, screeningMeta, docCounts) {
  const cId = String(c.id || c._id);
  const safeName = (c.name || 'Unknown').replace(/[^\w\s-]/g, '').replace(/\s+/g, '_');

  let panNumber = c.customFields?.panNumber || c.customFields?.PAN || '';
  let panAcknowledgementNumber = c.customFields?.panAcknowledgement || c.customFields?.PANAcknowledgement || '';
  let aadhaarNumber = c.customFields?.aadhaarNumber || c.customFields?.Aadhaar || '';
  let uanNumber = c.customFields?.uan || c.customFields?.UAN || '';
  let maritalStatus = c.customFields?.maritalStatus || c.customFields?.MaritalStatus || '';
  let completeAddress = c.customFields?.presentAddress || c.customFields?.PresentAddress || c.location || '';

  let panSubmissionId = null;
  let aadhaarSubmissionId = null;
  let resumeSubmissionId = null;
  let bankSubmissionId = null;
  let bankAccountNumber = '';
  let bankRead = null;

  for (const doc of Array.isArray(documents) ? documents : []) {
    if (doc.subtype === 'pan_card' || doc.subtype === 'aadhaar_or_pan') {
      if (doc.documentNumberKind === 'pan_acknowledgement') {
        if (!panAcknowledgementNumber && doc.documentNumber) panAcknowledgementNumber = doc.documentNumber;
      } else {
        if (!panNumber && doc.documentNumber) panNumber = doc.documentNumber;
        if (!panSubmissionId && doc.id && doc.subtype === 'pan_card') panSubmissionId = doc.id;
      }

      if (!panSubmissionId && doc.id && doc.subtype === 'aadhaar_or_pan') {
        if (doc.documentNumber && PAN_PATTERN.test(doc.documentNumber)) {
          panSubmissionId = doc.id;
          panNumber = doc.documentNumber;
        } else if (doc.documentNumber && AADHAAR_PATTERN.test(doc.documentNumber)) {
          aadhaarSubmissionId = doc.id;
          aadhaarNumber = doc.documentNumber;
        } else if (doc.documentNumberKind !== 'pan_acknowledgement') {
          panSubmissionId = doc.id;
        }
      }
    }
    if (doc.subtype === 'aadhaar_card') {
      if (!aadhaarNumber && doc.documentNumber) aadhaarNumber = doc.documentNumber;
      if (!aadhaarSubmissionId && doc.id) aadhaarSubmissionId = doc.id;
    }
    if (doc.subtype === 'uan_number' && !uanNumber) {
      uanNumber = doc.documentNumber || '';
    }
    if (doc.subtype === 'marital_status' && !maritalStatus) {
      maritalStatus = doc.documentNumber || '';
    }
    if (doc.subtype === 'complete_address') {
      // Complete address is used for both permanent and current address.
      completeAddress = doc.documentNumber || completeAddress;
    }
    if (doc.subtype === 'resume_cv') {
      if (!resumeSubmissionId && doc.id) resumeSubmissionId = doc.id;
    }
    if (doc.subtype === 'bank_account' && !bankSubmissionId && doc.id) {
      // Docs arrive newest first. bankDetails holds what the candidate confirmed (server-checked
      // against the read); older uploads have only the typed account number and the raw read,
      // whose holder could be page text such as "Savings", so it is not exported as the name.
      bankSubmissionId = doc.id;
      bankAccountNumber = doc.bankDetails?.accountNumber || doc.documentNumber || '';
      bankRead = doc.bankDetails
        ? {
            ifsc: doc.bankDetails.ifsc,
            accountHolderName: doc.bankDetails.holderName,
            bankName: doc.bankDetails.bankName,
          }
        : { ...(doc.verificationDetails?.bank || {}), accountHolderName: '' };
    }
  }

  const folderPrefix = panNumber ? panNumber.toUpperCase() : cId;
  const salary = salaryData || {};

  const dojField = salary.doj
    ? new Date(salary.doj).toLocaleDateString('en-GB')
    : c.customFields?.doj || c.customFields?.DOJ || c.onboardingQuarter || '';
  const grossSalaryField =
    salary.grossSalary ||
    c.currentCtcLacs ||
    c.expectedCtcLacs ||
    c.customFields?.grossSalary ||
    c.customFields?.GrossSalary ||
    '';

  const files = [];
  if (resumeSubmissionId) {
    files.push({ kind: 'RESUME', url: `/api/documents/download/${resumeSubmissionId}` });
  } else if (c.resumeUrl || c.resume?.url || c.resumeFileId) {
    files.push({ kind: 'RESUME', url: `/api/screenings/candidates/${cId}/download-resume`, resumeFallback: true });
  }
  if (panSubmissionId) files.push({ kind: 'PAN', url: `/api/documents/download/${panSubmissionId}` });
  if (aadhaarSubmissionId) files.push({ kind: 'AADHAAR', url: `/api/documents/download/${aadhaarSubmissionId}` });
  if (bankSubmissionId) files.push({ kind: 'BANK', url: `/api/documents/download/${bankSubmissionId}` });

  return {
    candidateId: cId,
    safeName,
    folderPrefix,
    folderName: `${folderPrefix}_${safeName}`,
    files,
    row: {
      storeCode: c.storeId || c.storeDetails?.name || '',
      aadhaar: aadhaarNumber || '',
      name: c.name || '',
      dob: c.dob ? new Date(c.dob).toLocaleDateString('en-GB') : '',
      gender: c.gender || '',
      maritalStatus,
      mobileNumber: c.phone || '',
      emailId: c.email || '',
      presentAddress: completeAddress,
      permanentAddress: completeAddress, // same as PresentAddress per requirements
      designation: resolveGoColorsDesignation(salary, screeningMeta),
      doj: dojField,
      grossSalary: grossSalaryField,
      pan: panNumber || '',
      panAcknowledgement: panAcknowledgementNumber || '',
      esic: c.customFields?.esic || c.customFields?.ESIC || '',
      uan: uanNumber,
      bankAccountNumber,
      bankIfsc: bankRead?.ifsc || '',
      bankHolderName: bankRead?.accountHolderName || '',
      bankName: bankRead?.bankName || '',
      documents: docCounts?.total ? `${docCounts.uploaded ?? 0}/${docCounts.total}` : '',
      selectedDate: c.shortlistedAt ? new Date(c.shortlistedAt).toLocaleDateString('en-GB') : '',
    },
  };
}

/** File extension from the downloaded blob's type, as the original per-type branches did. */
export function extensionFor(blobType, file) {
  const type = String(blobType || '');
  const isWord = type.includes('word') || type.includes('document');
  if (file.resumeFallback) return isWord ? '.docx' : '.pdf';
  if (file.kind === 'RESUME' && isWord) return '.docx';
  if (type.includes('pdf')) return '.pdf';
  if (type.includes('png')) return '.png';
  if (type.includes('jpeg') || type.includes('jpg')) return '.jpg';
  return '';
}

/** Two candidates can share a PAN + name (the same person in two screenings); keep both. */
export function uniqueFolderNames(entries) {
  const used = new Map();
  return entries.map((entry) => {
    const n = (used.get(entry.folderName) || 0) + 1;
    used.set(entry.folderName, n);
    return n === 1 ? entry.folderName : `${entry.folderName}_${n}`;
  });
}

async function mapWithConcurrency(items, limit, fn) {
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const i = next;
      next += 1;
      await fn(items[i], i);
    }
  });
  await Promise.all(workers);
}

export function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoking in the same tick can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
}

export function safeFileTitle(title) {
  return String(title || 'Report')
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '_');
}

// ── Fetching documents: every file the candidate has must reach the ZIP ──────────────────
// On 14 Oct 2026 a PAN the server sent with a 200 never reached a Go Colors ZIP: the
// transfer broke in the browser and the old code skipped the file with only a count in the
// toast. Now each file is retried, checked against the size the server sent, given a second
// pass, and a ZIP is saved only once every file is in, or after HR agrees to go without
// the ones named on screen.

const FETCH_ATTEMPTS = 4;
const FETCH_TIMEOUT_MS = 60 * 1000;
const MISSING_LIST_MAX = 10;

// Marks these fetches as the payroll Download, so the server counts them on its own per-user
// limit (rateLimiters.payrollRequestKind) instead of the office's shared global one.
const PAYROLL_REQUEST_PARAMS = Object.freeze({ for: 'payroll' });

// A 429 means "slow down", not "broken": wait as long as the server's Retry-After says (the
// limit's window is a minute) without spending a retry attempt, a few times at most.
const MAX_RATE_LIMIT_WAITS = 5;
const DEFAULT_RETRY_AFTER_MS = 30 * 1000;
const MAX_RETRY_AFTER_MS = 65 * 1000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Worth another try: no response (network, timeout, cut-off body) or a passing server error. 403/404 will not change. */
export function isRetryableFetchError(err) {
  const status = err?.response?.status;
  if (!status) return true;
  return status === 408 || status === 429 || status >= 500;
}

/** Exponential backoff with jitter: about 0.5 s, 1 s, 2 s, capped at 4 s. */
export function retryDelayMs(attempt, random = Math.random) {
  const base = Math.min(500 * 2 ** (attempt - 1), 4000);
  return base / 2 + random() * (base / 2);
}

/** How long to wait before retrying a rate-limited (429) fetch, or null when it was not one. */
export function rateLimitWaitMs(err, random = Math.random) {
  if (err?.response?.status !== 429) return null;
  const seconds = Number(err.response.headers?.['retry-after']);
  const ms = Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : DEFAULT_RETRY_AFTER_MS;
  // Up to 1 s of jitter so the parallel fetches do not all come back in the same instant.
  return Math.min(ms, MAX_RETRY_AFTER_MS) + random() * 1000;
}

function describeFetchError(err) {
  if (err?.response?.status) return `HTTP ${err.response.status}`;
  if (err?.code === 'ECONNABORTED') return 'timed out';
  return err?.message || 'network error';
}

/**
 * One document as a Blob, whole: an empty body or one shorter than the server's
 * X-Document-Bytes counts as a failure, and failures are retried with backoff. A 429 waits out
 * the server's Retry-After instead (up to MAX_RATE_LIMIT_WAITS times) and keeps its attempts.
 */
export async function fetchDocumentBlob(file, { attempts = FETCH_ATTEMPTS, wait = sleep } = {}) {
  let attempt = 1;
  let rateLimitWaits = 0;
  for (;;) {
    try {
      const res = await axios.get(file.url, {
        params: PAYROLL_REQUEST_PARAMS,
        responseType: 'blob',
        timeout: FETCH_TIMEOUT_MS,
      });
      const blob = res.data;
      const expected = Number(res.headers?.['x-document-bytes']);
      if (!blob || !blob.size) throw new Error('empty file');
      if (expected && blob.size !== expected) throw new Error(`incomplete file (${blob.size} of ${expected} bytes)`);
      return blob;
    } catch (err) {
      const limitedFor = rateLimitWaitMs(err);
      if (limitedFor !== null && rateLimitWaits < MAX_RATE_LIMIT_WAITS) {
        rateLimitWaits += 1;
        await wait(limitedFor);
        continue;
      }
      if (attempt >= attempts || !isRetryableFetchError(err)) throw err;
      await wait(retryDelayMs(attempt));
      attempt += 1;
    }
  }
}

/**
 * Download every document of `entries` (FETCH_CONCURRENCY candidates at a time). Files that
 * still fail get one more pass, one at a time, after the rest are in — by then a network blip
 * has usually passed.
 *
 * @returns {Promise<{ files: Array<Array<{ name: string, blob: Blob }>>, missing: Array<{ index: number, kind: string, error: string }> }>}
 *   `files[i]` belongs to `entries[i]`.
 */
export async function fetchPayrollDocuments(entries, { onProgress, fetchBlob = fetchDocumentBlob } = {}) {
  const files = entries.map(() => []);
  const failed = [];
  let done = 0;
  const add = (index, file, blob) =>
    files[index].push({ name: `${entries[index].folderPrefix}_${file.kind}${extensionFor(blob.type, file)}`, blob });

  await mapWithConcurrency(entries, FETCH_CONCURRENCY, async (entry, index) => {
    for (const file of entry.files) {
      try {
        add(index, file, await fetchBlob(file));
      } catch (err) {
        failed.push({ index, file });
      }
    }
    done += 1;
    if (onProgress) onProgress(done, entries.length);
  });

  const missing = [];
  for (const { index, file } of failed) {
    try {
      add(index, file, await fetchBlob(file));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`Could not fetch ${file.kind} for ${entries[index].safeName}`, err);
      missing.push({ index, kind: file.kind, error: describeFetchError(err) });
    }
  }
  return { files, missing };
}

/** Missing files grouped per candidate: what the confirm names and what the server logs. */
export function groupMissingByCandidate(entries, missing) {
  const byIndex = new Map();
  for (const m of missing) {
    const row = byIndex.get(m.index) || {
      candidateId: entries[m.index].candidateId,
      name: entries[m.index].row?.name || entries[m.index].safeName,
      missing: [],
      error: m.error,
    };
    row.missing.push(m.kind);
    byIndex.set(m.index, row);
  }
  return [...byIndex.values()];
}

export function missingSummary(incomplete, max = MISSING_LIST_MAX) {
  const lines = incomplete.slice(0, max).map((c) => `• ${c.name}: ${c.missing.join(', ')}`);
  if (incomplete.length > max) lines.push(`…and ${incomplete.length - max} more`);
  return lines.join('\n');
}

function confirmDownloadWithout(incomplete) {
  return window.confirm(
    'These documents could not be downloaded, even after several tries:\n\n' +
      `${missingSummary(incomplete)}\n\n` +
      'OK: save the ZIP without them. These candidates will NOT be marked "Downloaded", so you can download them again.\n' +
      'Cancel: stop. Nothing from this ZIP is saved. Check your internet connection and try again.'
  );
}

/** HR stopped a download rather than save a ZIP with documents missing. */
export class IncompleteDownloadError extends Error {
  constructor(incomplete) {
    super(`Download stopped: documents missing for ${incomplete.length} candidate(s)`);
    this.name = 'IncompleteDownloadError';
    this.incomplete = incomplete;
  }
}

/**
 * Build and save one ZIP from documents already fetched: a folder per entry plus the Excel at
 * the root.
 */
export async function saveGoColorsPayrollZip({ entries, files, excelName, zipName }) {
  const zip = new JSZip();
  const folderNames = uniqueFolderNames(entries);
  entries.forEach((entry, i) => {
    const folder = zip.folder(folderNames[i]);
    for (const f of files[i]) folder.file(f.name, f.blob);
  });
  zip.file(excelName, buildGoColorsWorkbookArray(entries.map((e) => e.row)));
  const blob = await zip.generateAsync({ type: 'blob' });
  saveBlob(blob, zipName);
}

/** The candidate Excel alone (no documents). */
export function saveGoColorsExcel({ entries, excelName }) {
  const buffer = buildGoColorsWorkbookArray(entries.map((e) => e.row));
  saveBlob(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    excelName
  );
}

export function chunkEntries(entries, size = MAX_CANDIDATES_PER_ZIP) {
  const parts = [];
  for (let i = 0; i < entries.length; i += size) parts.push(entries.slice(i, i + size));
  return parts;
}

/**
 * Save `entries` as one ZIP, or as numbered parts when there are more than
 * MAX_CANDIDATES_PER_ZIP. Each part's documents are all fetched and checked before its ZIP is
 * saved. If any are still missing, `confirmIncomplete(incomplete)` decides: false throws
 * IncompleteDownloadError and nothing of that part is saved; true saves it without them.
 * `onPartSaved(completeEntries, incomplete)` runs after each part is handed to the browser —
 * `completeEntries` only holds candidates with every document in the ZIP, so only they get
 * marked downloaded — and a failure in part 3 never un-records parts 1 and 2.
 *
 * @returns {Promise<{ parts: number, missingFiles: number, incomplete: Array }>}
 */
export async function saveGoColorsPayrollZipParts({
  entries,
  zipBase,
  excelBase,
  onPartSaved,
  onProgress,
  confirmIncomplete = confirmDownloadWithout,
  fetchBlob,
}) {
  const parts = chunkEntries(entries);
  let missingFiles = 0;
  const incompleteAll = [];
  for (let p = 0; p < parts.length; p += 1) {
    const part = parts[p];
    const suffix = parts.length > 1 ? `_part${p + 1}of${parts.length}` : '';
    const { files, missing } = await fetchPayrollDocuments(part, {
      fetchBlob,
      onProgress: onProgress ? (done, total) => onProgress({ part: p + 1, parts: parts.length, done, total }) : undefined,
    });
    const incomplete = groupMissingByCandidate(part, missing);
    if (incomplete.length && !(await confirmIncomplete(incomplete))) {
      throw new IncompleteDownloadError(incomplete);
    }
    await saveGoColorsPayrollZip({
      entries: part,
      files,
      excelName: `${excelBase}${suffix}.xlsx`,
      zipName: `${zipBase}${suffix}.zip`,
    });
    missingFiles += missing.length;
    incompleteAll.push(...incomplete);
    const incompleteIds = new Set(incomplete.map((c) => c.candidateId));
    if (onPartSaved) await onPartSaved(part.filter((e) => !incompleteIds.has(e.candidateId)), incomplete);
  }
  return { parts: parts.length, missingFiles, incomplete: incompleteAll };
}

/**
 * Tell the server these candidates' payroll package was downloaded. The server only marks the
 * ones with filled salary details and returns their ids. `downloaded: false` sets them back to
 * "not downloaded" (the chip edited by hand). `incomplete` (candidates saved without some
 * documents, never in `candidateIds`) is only logged by the server.
 *
 * @returns {Promise<{ candidateIds: string[], at: string, downloaded: boolean } | null>} null when nothing was sent
 */
export async function recordPayrollDownload(candidateIds, downloaded = true, manual = false, incomplete = []) {
  if (!candidateIds.length && !incomplete.length) return null;
  const res = await axios.post('/api/arm-salary/payroll-downloaded', {
    candidateIds,
    downloaded,
    manual,
    ...(incomplete.length ? { incomplete } : {}),
  });
  return { candidateIds: res.data?.candidateIds || [], at: res.data?.at || new Date().toISOString(), downloaded };
}

// ── Job page Download → Filled: which candidates ─────────────────────────────────────────
// Date choices are IST calendar days of when the ARM submitted salary; the server reads them
// the same way (gcHiring.service.parseIstDayRange).

export const FILLED_SCOPES = Object.freeze({
  NOT_DOWNLOADED: 'not_downloaded',
  LAST_7_DAYS: 'last_7_days',
  THIS_MONTH: 'this_month',
  CUSTOM: 'custom',
});

/** Today's date in India as 'YYYY-MM-DD'. */
export function istToday(now = new Date()) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(now);
}

function shiftIsoDay(iso, days) {
  return new Date(Date.parse(`${iso}T00:00:00Z`) + days * 86400000).toISOString().slice(0, 10);
}

/**
 * The `from`/`to` query for a Filled scope, `null` for "not downloaded yet", or
 * `{ error }` when a custom range is incomplete or backwards.
 */
export function filledRangeFor(scope, { today, from, to } = {}) {
  switch (scope) {
    case FILLED_SCOPES.NOT_DOWNLOADED:
      return null;
    case FILLED_SCOPES.LAST_7_DAYS:
      return { from: shiftIsoDay(today, -6), to: today };
    case FILLED_SCOPES.THIS_MONTH:
      return { from: `${today.slice(0, 8)}01`, to: today };
    case FILLED_SCOPES.CUSTOM:
      if (!from || !to) return { error: 'Pick both a start and an end date.' };
      if (from > to) return { error: 'The start date must be on or before the end date.' };
      return { from, to };
    default:
      return { error: 'Choose what to download.' };
  }
}
